import DateTimePicker from '@/components/DatePicker'
import { Form, FormControl, FormField, FormItem, FormMessage } from '@/components/ui/Form'
import { Input } from '@/components/ui/Input'
import CustomSelect from '@/components/ui/Select'
import { cn } from '@/lib/utils'
import { ICON_MAPPING } from '@/utils/polls/getPollsOptions'
import { Plus, X } from 'lucide-react'
import { useMemo } from 'react'
import { useFieldArray, type UseFormReturn } from 'react-hook-form'
import { getMaxSupply } from '@/config/get-max-supply.ts'
import { usePepperSupply } from '@/hooks/pepper-contract/usePepperSupply.ts'
import { formatEther } from 'viem'
import { formatToHumanReadableNumber } from '@/utils/human-readable-number.ts'

interface PollFormProps<TForm extends UseFormReturn<any>> {
	form: TForm
	variant: 'new' | 'upcoming' | 'ongoing'
}

type Field = {
	value: string
	id: string
}

const DEFAULT_OPTIONS = Object.entries(ICON_MAPPING).map(([value, icon]) => ({ value, text: value, icon }))

function buildCustomOptions<T extends any[]>(fields: T) {
	return fields
		.filter((field: Field) => Boolean(field.value) && !DEFAULT_OPTIONS.some(opt => opt.value === field.value))
		.map((field: Field) => {
			return {
				icon: ICON_MAPPING[field.value] || '/images/custom-answer.webp',
				text: field.value,
				value: field.value
			}
		})
}

export function PollForm<TForm extends UseFormReturn<any>>({ form, variant }: PollFormProps<TForm>) {
	const { fields, append, remove } = useFieldArray({
		name: 'answers',
		control: form.control
	})

	const answerOpts = useMemo(() => [...DEFAULT_OPTIONS, ...buildCustomOptions(fields)], [fields])

	const isNewPoll = variant === 'new'
	const isOngoingPoll = variant === 'ongoing'

	const { maxSupply } = usePepperSupply()
	const maxVotingCap = maxSupply.status === 'success' ? parseInt(formatEther(maxSupply.data)) : getMaxSupply()

	return (
		<Form {...form}>
			<form noValidate>
				<p className="text-2xl text-neutral-50 font-semibold font-pp-neue-bit leading-5 mb-5 uppercase">
					Poll question
				</p>

				<FormField
					control={form.control}
					name="question"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<Input disabled={!isNewPoll} placeholder="PEPPER" type="text" {...field} />
							</FormControl>
							<FormMessage className="text-[1rem]" />
						</FormItem>
					)}
				/>

				<p className="text-2xl text-neutral-50 font-semibold font-pp-neue-bit leading-5 mt-6 mb-5 uppercase">
					Poll Answers
				</p>

				<div
					className={cn('flex flex-col gap-3', {
						'opacity-50 pointer-events-none': isOngoingPoll
					})}
				>
					{fields.map((f, index) => (
						<FormField
							key={f.id}
							control={form.control}
							name={`answers.${index}.value`}
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<div className="relative">
											<CustomSelect
												options={answerOpts}
												value={field.value}
												onChange={field.onChange}
												index={index}
												placeholder={`Answer ${index + 1}`}
												disabled={isOngoingPoll}
											/>
											<button
												disabled={isOngoingPoll}
												type="button"
												onClick={() => remove(index)}
												className="size-4 hover:bg-neutral-30 rounded-full flex justify-center items-center group absolute top-1/2 right-12 -translate-y-1/2"
											>
												<X size={20} className="text-neutral-30 group-hover:text-neutral-10" />
											</button>
										</div>
									</FormControl>
									<FormMessage className="text-[1rem]" />
								</FormItem>
							)}
						/>
					))}

					<div className="mt-4 flex justify-end">
						<button
							disabled={isOngoingPoll}
							type="button"
							onClick={() => append({ value: '' })}
							className="w-full h-11 flex justify-center items-center rounded-md border border-dashed border-black/10"
						>
							<Plus size={20} className="text-black/30" />
						</button>
					</div>
				</div>

				<p className="text-2xl text-neutral-50 font-semibold font-pp-neue-bit leading-5 mt-6 mb-5 uppercase">
					Poll cap
				</p>

				<FormField
					control={form.control}
					name="votingCap"
					render={({ field }) => (
						<FormItem>
							<FormControl>
								<Input
									{...field}
									disabled={isOngoingPoll}
									placeholder={maxVotingCap.toLocaleString('en-US')}
									type="text"
									value={field.value !== 0 ? field.value.toLocaleString('en-US') : ''}
									onChange={evt => {
										const newValue = evt.target.value.replace(/\D/g, '')
										field.onChange(newValue !== '' ? parseInt(newValue) : 0)
									}}
								/>
							</FormControl>
							<FormMessage className="text-[1rem]">
								{formatToHumanReadableNumber(field.value) !== ''
									? `Number preview: ${formatToHumanReadableNumber(field.value)}`
									: undefined}
							</FormMessage>
						</FormItem>
					)}
				/>

				<div className="mt-6">
					<div className="flex flex-col gap-5">
						<FormField
							control={form.control}
							name="startTime"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<DateTimePicker
											disabled={isOngoingPoll}
											selectedDate={field.value}
											selectedTime={field.value}
											label="POLL START"
											onDateTimeChange={e => {
												field.onChange(e)
											}}
											invalid={!!form.formState.errors.startTime}
										/>
									</FormControl>
									<FormMessage className="text-[1rem]" />
								</FormItem>
							)}
						/>

						<FormField
							control={form.control}
							name="endTime"
							render={({ field }) => (
								<FormItem>
									<FormControl>
										<DateTimePicker
											selectedDate={field.value}
											selectedTime={field.value}
											label="POLL END"
											onDateTimeChange={e => {
												field.onChange(e)
											}}
											invalid={!!form.formState.errors.endTime}
										/>
									</FormControl>
									<FormMessage className="text-[1rem]" />
								</FormItem>
							)}
						/>
					</div>
				</div>
			</form>
		</Form>
	)
}
